/**
 * Cadeau Verpakking Toggle
 *
 * Integrates with Dawn's variant selection system.
 * - Shows a toggle when the current variant has a counterpart with Cadeauverpakking = "Verpakking"
 * - Switching the toggle programmatically changes the variant via Dawn's existing machinery
 * - Displays the price difference next to the toggle (currency-aware via Shopify money format)
 * - Switches the product image to the one with alt text "cadeau" when toggled on
 */

(function () {
  const OPTION_NAME = 'Cadeauverpakking';
  const VALUE_JA = 'Giftbox';
  const VALUE_NEE = 'Nee';

  if (customElements.get('cadeau-verpakking')) return;

  class CadeauVerpakking extends HTMLElement {
    connectedCallback() {
      const raw = this.querySelector('[data-cadeau-variants]');
      if (!raw) return;

      try {
        this._data = JSON.parse(raw.textContent);
      } catch {
        return;
      }

       this._verpakkingIndex = this._data.optionNames.findIndex((n) =>
        ['Cadeauverpakking', 'Geschenkverpackung', 'Emballage cadeau', 'Opakowanie prezentowe'].includes(n)
      );
      if (this._verpakkingIndex === -1) return;

      
      this._optionName = this._data.optionNames[this._verpakkingIndex];
      const _vals = [...new Set(this._data.variants.map((v) => v.options[this._verpakkingIndex]))];
      this._valueJa = _vals.find((v) => ['Giftbox', 'Giftbox', 'Boîte cadeau', 'Pudełko prezentowe'].includes(v)) ?? VALUE_JA;
      this._valueNee = _vals.find((v) => ['Nee', 'Nein', 'Non', 'Nie'].includes(v)) ?? VALUE_NEE;

      this._toggleInput = this.querySelector('.cadeau-toggle__input');
      this._priceEl = this.querySelector('.cadeau-toggle__price');
      if (!this._toggleInput) return;

      this._toggleBezig = false;

      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsubscribe = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this._onVariantSelectionChange.bind(this)
        );
      }

      this._toggleInput.addEventListener('change', this._onToggle.bind(this));
      this._hideNativePicker();
      this._refresh();
    }

    disconnectedCallback() {
      this._unsubscribe?.();
    }

    _onVariantSelectionChange({ data: { event } }) {
      const variantSelects = this._variantSelects();
      if (!variantSelects || !variantSelects.contains(event.target)) return;
      if (this._toggleBezig) return;
      this._refresh();
    }

    _refresh() {
      const variantSelects = this._variantSelects();
      if (!variantSelects) return;

      const selectedOptions = this._readPickerOptions(variantSelects);
      const currentVariant = this._findVariantByOptions(selectedOptions);
      if (!currentVariant) { this._hide(); return; }

      this._currentVariant = currentVariant;

       const isJa = currentVariant.options[this._verpakkingIndex] === this._valueJa;
      const jaVariant = isJa ? currentVariant : this._findCounterpart(currentVariant, this._valueJa);

      if (!jaVariant) { this._hide(); return; }

      this._show();
      this._toggleInput.checked = isJa;
      this._updatePrice(currentVariant, jaVariant, isJa);
    }

    _onToggle() {
      if (!this._currentVariant) return;

      const targetValue = this._toggleInput.checked ? this._valueJa : this._valueNee;
      const targetVariant = this._findCounterpart(this._currentVariant, targetValue);
      if (!targetVariant) {
        this._toggleInput.checked = !this._toggleInput.checked;
        return;
      }

      this._toggleBezig = true;
      this._selectVariantInPicker(targetValue);
      this._toggleBezig = false;

      this._currentVariant = targetVariant;

      if (this._toggleInput.checked) {
        this._switchToCadeauImage();
      } else {
        this._restoreDefaultImage();
      }
    }

    _readPickerOptions(variantSelects) {
      const values = {};
      variantSelects.querySelectorAll('select').forEach((select) => {
        const match = select.name.match(/^options\[(.+)\]$/);
        if (match) values[match[1]] = select.value;
      });
      variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
        const legend = fieldset.querySelector('legend.form__label');
        if (!legend) return;
        const checked = fieldset.querySelector('input:checked');
        if (!checked) return;
        const clone = legend.cloneNode(true);
        clone.querySelector('[data-selected-value]')?.remove();
        const name = clone.textContent.trim().replace(/:$/, '').trim();
        if (name) values[name] = checked.value;
      });
      return values;
    }

    _findVariantByOptions(selectedOptions) {
      const { optionNames, variants } = this._data;
      return variants.find((v) =>
        optionNames.every((name, i) => {
          if (!(name in selectedOptions)) return true;
          return v.options[i] === selectedOptions[name];
        })
      ) ?? null;
    }

    _findCounterpart(currentVariant, targetValue) {
      const idx = this._verpakkingIndex;
      return this._data.variants.find((v) => {
        if (v.id === currentVariant.id) return false;
        if (v.options[idx] !== targetValue) return false;
        return v.options.every((val, i) => i === idx || val === currentVariant.options[i]);
      }) ?? null;
    }

    _updatePrice(currentVariant, jaVariant, isJa) {
      if (!this._priceEl) return;
      const neeVariant = isJa
        ? (this._findCounterpart(currentVariant, this._valueNee) ?? currentVariant)
        : currentVariant;
      const diff = jaVariant.price - neeVariant.price;
      this._priceEl.textContent = diff > 0 ? `+ ${this._formatMoney(diff)}` : '';
    }

    /**
     * Formats cents into the shop's active money format (currency-agnostic).
     * Reads a hidden <span data-money-format> rendered server-side via
     * Liquid's {{ 100 | money }}, so it automatically follows whatever
     * currency/format the active Market applies — no per-country JS logic needed.
     *
     * Required snippet somewhere on the page (once, hidden):
     *   <span data-money-format hidden>{{ 100 | money }}</span>
     */
    _formatMoney(cents) {
      const template = document.querySelector('[data-money-format]')?.textContent?.trim();
      if (!template) {
        // Fallback if the placeholder isn't present in the DOM.
        return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
      }
      const match = template.match(/\d+(?:[.,]\d+)?/);
      if (!match) return template;
      const usesComma = match[0].includes(',');
      const value = (cents / 100).toFixed(2);
      return template.replace(match[0], usesComma ? value.replace('.', ',') : value);
    }

    _selectVariantInPicker(targetValue) {
      const variantSelects = this._variantSelects();
      if (!variantSelects) return;
      for (const select of variantSelects.querySelectorAll('select')) {
        const match = select.name.match(/^options\[(.+)\]$/);
       if (match?.[1] === this._optionName) {
          select.value = targetValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
      for (const fieldset of variantSelects.querySelectorAll('fieldset')) {
        const legend = fieldset.querySelector('legend.form__label');
        if (!legend) continue;
        const clone = legend.cloneNode(true);
        clone.querySelector('[data-selected-value]')?.remove();
        const name = clone.textContent.trim().replace(/:$/, '').trim();
       if (name === this._optionName) {
          const radio = fieldset.querySelector(`input[type="radio"][value="${CSS.escape(targetValue)}"]`);
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
      }
    }

    _hideNativePicker() {
      const variantSelects = this._variantSelects();
      if (!variantSelects) return;
      const dropdown = variantSelects.querySelector(
              `select[name="options[${this._optionName}]"]`
      )?.closest('.product-form__input');
      if (dropdown) { dropdown.style.display = 'none'; return; }
      for (const fieldset of variantSelects.querySelectorAll('fieldset.product-form__input')) {
      if (fieldset.querySelector(`input[name^="${this._optionName}-"]`)) {
          fieldset.style.display = 'none';
          return;
        }
        const legend = fieldset.querySelector('legend');
        if (legend) {
          const clone = legend.cloneNode(true);
          clone.querySelector('[data-selected-value]')?.remove();
          const text = clone.textContent.trim().replace(/:$/, '').trim();
          if (text === this._optionName) { fieldset.style.display = 'none'; return; }
        }
      }
    }

    _switchToCadeauImage() {
      const mediaList = document.querySelector(
        `#MediaGallery-${this.dataset.section} .product__media-list, .product__media-list`
      );
      if (!mediaList) return;
      const cadeauItem = Array.from(
        mediaList.querySelectorAll('.product__media-item, [data-media-id]')
      ).find((el) => el.querySelector('img')?.alt?.toLowerCase().includes('cadeau'));
      if (!cadeauItem) return;
      const mediaId = cadeauItem.dataset.mediaId || cadeauItem.closest('[data-media-id]')?.dataset.mediaId;
      if (!mediaId) return;
      const thumb = document.querySelector(
        `[data-target="#GalleryViewer-${this.dataset.section}"][data-media-id="${mediaId}"],
         button.thumbnail[data-media-id="${mediaId}"]`
      );
      if (thumb) { thumb.click(); } else {
        cadeauItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
      this._previousMediaId = mediaId;
    }

    _restoreDefaultImage() {
      const firstThumb = document.querySelector(
        `[data-target="#GalleryViewer-${this.dataset.section}"].thumbnail,
         .thumbnail-list__item:first-child button.thumbnail`
      );
      if (firstThumb) firstThumb.click();
    }

    _variantSelects() {
      return document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
    }

    _show() { this.removeAttribute('hidden'); }
    _hide() {
      this.setAttribute('hidden', '');
      if (this._toggleInput) this._toggleInput.checked = false;
    }
  }

  customElements.define('cadeau-verpakking', CadeauVerpakking);
})();